import { isBefore } from 'date-fns';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { csrClient } from '../../../apollo/api/avantArteClient';
import { useSessionStorage } from '../../../hooks/useSessionStorage';
import { GeneratedPrivateLink } from '../../../types/generated';
import { AvantArtePrivateSalesGateway } from '../../private-sales/gateways/AvantArtePrivateSalesGateway';
import { privateRoomPath, PrivateRoomQuery } from '../utils/privateRoomQuery';
import { PrivateRoomHandoff } from './useCheckoutHandoff';

const privateSalesGateway = new AvantArtePrivateSalesGateway(csrClient);

type UsePrivateRoomCheckoutArgs = {
  privateRoom: PrivateRoomQuery | null;
  productShopifyId: string;
};

export type PrivateRoomCheckoutController = {
  leaveIfExpired: () => boolean;
  loading: boolean;
  room: PrivateRoomHandoff | null;
};

const isExpiredAt = (expiresAt: string | null | undefined): boolean => {
  return Boolean(expiresAt && isBefore(new Date(expiresAt), new Date()));
};

const toUnlockCodes = (
  privateLink: GeneratedPrivateLink,
): Record<string, string> => {
  return Object.fromEntries(
    (privateLink.products ?? []).flatMap((product) => {
      return product.shopifyVariantId
        ? [[product.shopifyVariantId, product.unlockCode]]
        : [];
    }),
  );
};

export const usePrivateRoomCheckout = ({
  privateRoom,
  productShopifyId,
}: UsePrivateRoomCheckoutArgs): PrivateRoomCheckoutController => {
  const router = useRouter();
  const { getSessionStorage } = useSessionStorage();
  const [room, setRoom] = useState<PrivateRoomHandoff | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const opened =
    privateRoom &&
    room?.slug === privateRoom.slug &&
    room.unlockCodes[privateRoom.variantId]
      ? room
      : null;

  useEffect(() => {
    if (!privateRoom || opened || redirecting) return undefined;

    let cancelled = false;
    const { slug, variantId } = privateRoom;

    void privateSalesGateway
      .getPrivateLinkWithProducts(slug, getSessionStorage(slug) ?? '')
      .then(({ privateLink }) => {
        if (cancelled) return;

        const unlockCodes =
          privateLink && !isExpiredAt(privateLink.expiresAt)
            ? toUnlockCodes(privateLink)
            : {};

        if (privateLink && unlockCodes[variantId]) {
          setExpiresAt(privateLink.expiresAt ?? null);
          setRoom({ slug, unlockCodes });
          return;
        }

        setRedirecting(true);
        void router.replace(privateRoomPath(slug, productShopifyId));
      });

    return () => {
      cancelled = true;
    };
  }, [
    getSessionStorage,
    opened,
    privateRoom,
    productShopifyId,
    redirecting,
    router,
  ]);

  const leaveIfExpired = useCallback(() => {
    if (!privateRoom || !isExpiredAt(expiresAt)) return false;

    setRedirecting(true);
    void router.replace(privateRoomPath(privateRoom.slug, productShopifyId));

    return true;
  }, [expiresAt, privateRoom, productShopifyId, router]);

  return useMemo(
    () => ({
      leaveIfExpired,
      loading: privateRoom !== null && opened === null,
      room: opened,
    }),
    [leaveIfExpired, opened, privateRoom],
  );
};
